import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import ConfirmModal from '../components/common/ConfirmModal';
import PasswordStrengthMeter from '../components/common/PasswordStrengthMeter';
import {
  getSystemAdmins, createSystemAdmin, toggleSystemAdmin,
} from '../utils/api';
import { formatDate } from '../utils/helpers';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import {
  Shield, Plus, UserX, UserCheck, X, Eye, EyeOff, RefreshCw,
} from 'lucide-react';

export default function SystemAdminsPage() {
  const { user: me } = useAuth();
  const isMobile = useIsMobile();
  const [admins,      setAdmins]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showModal,   setShowModal]   = useState(false);
  const [busyId,      setBusyId]      = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSystemAdmins();
      setAdmins(data.admins || []);
    } catch { toast.error('Failed to load system admins'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (admin) => {
    if (admin.id === me?.id) { toast.error("You can't deactivate your own account"); return; }
    const action = admin.is_active ? 'deactivate' : 'reactivate';
    if (admin.is_active) {
      setConfirmAction({
        title: 'Deactivate Admin',
        message: `Deactivate ${admin.email}? They will immediately lose access.`,
        confirmLabel: 'Deactivate',
        danger: true,
        onConfirm: async () => {
          setBusyId(admin.id);
          try { await toggleSystemAdmin(admin.id, false); toast.success('Admin deactivated'); await load(); }
          catch (err) { toast.error(err.message || 'Failed to deactivate'); }
          finally { setBusyId(null); }
        },
      });
      return;
    }
    setBusyId(admin.id);
    try { await toggleSystemAdmin(admin.id, true); toast.success('Admin reactivated'); await load(); }
    catch (err) { toast.error(err.message || 'Failed to reactivate'); }
    finally { setBusyId(null); }
  };

  return (
    <Layout title="System Admins" crumb={`${admins.length} admin${admins.length !== 1 ? 's' : ''}`}>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          System Admins have full access to the ZeusCMS System Admin console.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={load}>
            <RefreshCw size={13} /> Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
            <Plus size={13} /> Add Admin
          </button>
        </div>
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      {loading ? (
        <div className="loading-center"><div className="spinner" /> Loading…</div>
      ) : !isMobile ? (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Admin</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {admins.map(admin => {
                  const isMe = admin.id === me?.id;
                  const busy = busyId === admin.id;
                  return (
                    <tr key={admin.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="sidebar-avatar" style={{ width: 28, height: 28, fontSize: '0.62rem' }}>
                            {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase() || '?'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                              {admin.first_name} {admin.last_name}
                              {isMe && <span className="badge badge-gold" style={{ marginLeft: 8 }}>You</span>}
                            </div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-muted)', marginTop: 1 }}>
                              <Shield size={9} style={{ marginRight: 3, verticalAlign: '-1px', color: 'var(--gold)' }} />
                              System Admin
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="mono">{admin.email}</td>
                      <td>
                        {admin.is_active
                          ? <span className="badge badge-green">Active</span>
                          : <span className="badge badge-red">Inactive</span>}
                      </td>
                      <td className="mono-sm">{admin.last_login ? formatDate(admin.last_login) : '—'}</td>
                      <td className="mono-sm">{formatDate(admin.created_at)}</td>
                      <td>
                        {!isMe && (
                          <button
                            className={`btn btn-sm ${admin.is_active ? 'btn-danger' : 'btn-success'}`}
                            disabled={busy}
                            onClick={() => handleToggle(admin)}
                            title={admin.is_active ? 'Deactivate' : 'Reactivate'}
                          >
                            {busy
                              ? <span className="spinner" style={{ width: 12, height: 12 }} />
                              : admin.is_active
                                ? <><UserX size={12} /> Deactivate</>
                                : <><UserCheck size={12} /> Reactivate</>}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {admins.length === 0 && (
                  <tr><td colSpan={6}>
                    <div className="empty">
                      <Shield size={28} />
                      <h3>No system admins found</h3>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Mobile card list */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {admins.length === 0 ? (
            <div className="empty"><Shield size={28} /><h3>No system admins</h3></div>
          ) : admins.map(admin => {
            const isMe = admin.id === me?.id;
            const busy = busyId === admin.id;
            return (
              <div key={admin.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="sidebar-avatar" style={{ width: 36, height: 36, fontSize: '0.7rem', flexShrink: 0 }}>
                  {(admin.first_name?.[0] || '').toUpperCase()}{(admin.last_name?.[0] || '').toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{admin.first_name} {admin.last_name}</span>
                    {isMe && <span className="badge badge-gold">You</span>}
                    {admin.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-red">Inactive</span>}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{admin.email}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                    Last login: {admin.last_login ? formatDate(admin.last_login) : 'Never'}
                  </div>
                </div>
                {!isMe && (
                  <button className={`btn btn-sm ${admin.is_active ? 'btn-danger' : 'btn-success'}`}
                    disabled={busy} onClick={() => handleToggle(admin)} style={{ flexShrink: 0 }}>
                    {busy ? <span className="spinner" style={{ width: 12, height: 12 }} />
                      : admin.is_active ? <><UserX size={12} /></> : <><UserCheck size={12} /></>}
                  </button>
                )}
              </div>
            );
          })}
        </div>

      )}

      {/* Add Admin Modal */}
      {showModal && (
        <AddAdminModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}

      <ConfirmModal
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => { await confirmAction?.onConfirm(); setConfirmAction(null); }}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel || 'Confirm'}
        danger={confirmAction?.danger !== false}
      />
    </Layout>
  );
}

function AddAdminModal({ onClose, onCreated }) {
  const [form,     setForm]     = useState({ first_name: '', last_name: '', email: '', password: '' });
  const [saving,   setSaving]   = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Email and password are required'); return; }
    if (form.password.length < 12)      { setError('Password must be at least 12 characters'); return; }
    setSaving(true);
    try {
      await createSystemAdmin(form);
      toast.success(`${form.email} added as System Admin`);
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to create admin');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">
            <Shield size={13} style={{ marginRight: 6, verticalAlign: '-1px', color: 'var(--gold)' }} />
            Add System Admin
          </span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '2px 6px' }}>
            <X size={14} />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div style={{ background: 'var(--red-dim)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--r-md)', padding: '10px 12px', fontSize: '0.78rem', color: 'var(--red)', marginBottom: 16 }}>
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input className="form-input" value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First" />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input className="form-input" value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last" />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="admin@example.com" />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => set('password', e.target.value)}
                placeholder="Min. 12 characters"
                style={{ paddingRight: 38 }}
              />
              <button
                type="button"
                onClick={() => setShowPass(s => !s)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}
              >
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <PasswordStrengthMeter password={form.password} />
          </div>

          <div style={{ background: 'var(--gold-dim)', border: '1px solid var(--border-gold)', borderRadius: 'var(--r-md)', padding: '10px 12px', fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            <Shield size={11} style={{ marginRight: 5, verticalAlign: '-1px', color: 'var(--gold)' }} />
            This person will have full System Admin access to the ZeusCMS console.
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Creating…</> : <><Plus size={13} /> Add Admin</>}
          </button>
        </div>
      </div>
    </div>
  );
}
